// src/Utils/generatePriceQuotationPdf.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const generatePriceQuotationPdf = (quotations, logoBase64 = null) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const pageW = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // ── COLORS ──────────────────────────────────────────────────────────────────
  const YELLOW = [255, 230, 0];
  const DARK_BLUE = [0, 32, 96];
  const HEADER_BG = [0, 70, 160];
  const HEADER_TEXT = [255, 255, 0];
  const ALT_ROW = [240, 248, 255];
  const WHITE = [255, 255, 255];
  const BORDER = [180, 200, 220];
  const TEXT_DARK = [20, 20, 60];

  let cursorY = 6;

  // ── LOGO (top-left) + COMPANY NAME HEADER ───────────────────────────────────
  // Yellow background banner for company name
  doc.setFillColor(...YELLOW);
  doc.rect(0, cursorY, pageW, 14, "F");

  // Logo (top-left inside the yellow band)
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, "PNG", 6, cursorY + 1, 28, 12);
    } catch (_) {
      /* skip if image fails */
    }
  }

  // Company name centred on the yellow band
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...DARK_BLUE);
  doc.text("PCM Exports", pageW / 2, cursorY + 9.5, { align: "center" });

  cursorY += 14;

  // Address row – white background
  doc.setFillColor(...WHITE);
  doc.rect(0, cursorY, pageW, 8, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_DARK);
  doc.text(
    "Shop No. 104, Block-A, Bharati Tower, Forestpark, Ekamra Marg, Bhimpur, Bhubaneswar, Odisha(India) - 751009",
    pageW / 2,
    cursorY + 5,
    { align: "center" },
  );

  cursorY += 8;

  // Date row – light yellow background
  doc.setFillColor(255, 245, 150);
  doc.rect(0, cursorY, pageW, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...DARK_BLUE);
  doc.text(`As On : ${today}`, pageW / 2, cursorY + 4.8, { align: "center" });

  cursorY += 7;

  // ── TABLE ────────────────────────────────────────────────────────────────────
  const columns = [
    { header: "Item Details", dataKey: "item" },
    { header: "P2( Weight )", dataKey: "weight" },
    { header: "P5( Batch No )", dataKey: "batch" },
    { header: "MRP", dataKey: "mrp" },
    { header: "Sale Price", dataKey: "sale" },
  ];

  const rows = quotations.map((q) => ({
    item: q.itemDetails || "",
    weight: q.P2_weight != null ? `${q.P2_weight}${q.weight_Type || ""}` : "",
    batch: q.p5_Batch || "",
    mrp: q.MRP != null ? q.MRP.toFixed(2) : "",
    sale: q.sale_Price != null ? q.sale_Price.toFixed(4) : "",
  }));

  autoTable(doc, {
    startY: cursorY,
    columns,
    body: rows,
    theme: "grid",
    tableWidth: "auto",
    margin: { left: 8, right: 8 },

    // Column widths proportional to image
    columnStyles: {
      item: { cellWidth: 68, fontStyle: "bold" },
      weight: { cellWidth: 28, halign: "center" },
      batch: { cellWidth: 38, halign: "center" },
      mrp: { cellWidth: 26, halign: "right" },
      sale: { cellWidth: 28, halign: "right" },
    },

    headStyles: {
      fillColor: HEADER_BG,
      textColor: HEADER_TEXT,
      fontStyle: "bold",
      fontSize: 8.5,
      halign: "center",
      cellPadding: 2.5,
      lineWidth: 0.3,
      lineColor: BORDER,
    },

    bodyStyles: {
      fontSize: 8,
      textColor: TEXT_DARK,
      cellPadding: 2,
      lineWidth: 0.2,
      lineColor: BORDER,
    },

    // Alternating row colours
    didParseCell(data) {
      if (data.section === "body") {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? WHITE : ALT_ROW;
      }
    },

    // Bold item name in first column
    willDrawCell(data) {
      if (data.section === "body" && data.column.dataKey === "item") {
        if (data.cell.raw && data.cell.raw.toString().trim() !== "") {
          data.cell.styles.fontStyle = "bold";
        }
      }
    },

    // Footer on every page
    didDrawPage(data) {
      const footerY = doc.internal.pageSize.getHeight() - 8;
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont("helvetica", "normal");
      doc.text(
        `PCM Exports  |  Price List  |  Generated on ${today}`,
        pageW / 2,
        footerY,
        { align: "center" },
      );
      doc.text(`Page ${data.pageNumber}`, pageW - 10, footerY, {
        align: "right",
      });
    },
  });

  doc.save(`PCM_Exports_PriceQuotation_${today.replace(/\//g, "-")}.pdf`);
};
